import axios from '@sas/nova-core/axios';
import { getLogger } from '@sas/nova-core/logging';
const logger = getLogger('FileUploader'); // eslint-disable-line @sas/i18n/jsx-to-localize

// ************************************
//
// checkFiles functions
//
// ************************************
const computeFileSize = file => {
  return file.size / 1024 / 1024;
};
const fireMaxFileSizeExceedEvent = (maxFileSizeExceededFile, maxFileSize, onFileSizeExceeded) => {
  const maxSize = maxFileSize; // props.maximumFileSize;
  logger.info(`File: ${maxFileSizeExceededFile.name} is of size ${maxFileSizeExceededFile.size} MB which exceeds the file size limit of ${maxSize} MB.`);
  onFileSizeExceeded?.({
    fileName: maxFileSizeExceededFile.name,
    fileSize: maxFileSizeExceededFile.size
  });
};
const isFileTypeRestricted = (fileName, restrictedFileTypes, onTypeMissmatch) => {
  const finalIndex = fileName.lastIndexOf('.');
  const fileEnding = finalIndex === -1 ? '' : fileName.substring(finalIndex + 1);
  for (const restrictedFileType of restrictedFileTypes) {
    if (fileEnding.toLowerCase() === restrictedFileType.toLowerCase()) {
      logger.info(`File: ${fileName} is of type ${fileEnding}.  The restricted file types are: ${restrictedFileTypes}.`);
      onTypeMissmatch?.({
        fileName,
        fileType: fileEnding
      });
      return true;
    }
  }
  return false;
};
const isFileTypeBad = (fileName, fileTypes, onTypeMissmatch) => {
  const finalIndex = fileName.lastIndexOf('.');
  const fileEnding = finalIndex === -1 ? '' : fileName.substring(finalIndex + 1);
  let wrongType = true;

  // check file types
  if (fileTypes && fileTypes.length > 0) {
    for (const fileType of fileTypes) {
      if (fileEnding.toLowerCase() === fileType.toLowerCase()) {
        wrongType = false;
      }
    }
    if (wrongType) {
      logger.info(`File: ${fileName} is of type ${fileEnding}.  The allowed file types are: ${fileTypes}.`);
      onTypeMissmatch?.({
        fileName,
        fileType: fileEnding
      });
      return true; // file type is bad
    }
  }

  return false;
};
const isFileNameTooLong = (fileName, maximumFilenameLength, onFilenameLengthExceeded) => {
  const iMaxFilenameLength = maximumFilenameLength ?? 0;
  if (iMaxFilenameLength !== 0 && fileName.length > iMaxFilenameLength) {
    logger.info(`The filename of ${fileName} (${fileName.length} characters) is longer than the maximum of ${iMaxFilenameLength} characters.`);
    onFilenameLengthExceeded?.({
      fileName
    });
    return true;
  }
  return false;
};
const areFilesAllowed = (files, fileTypeList, restrictedFileTypeList, maxFileLength, onFilenameLengthExceeded, onTypeMissmatch) => {
  const fileTypes = fileTypeList ?? [];
  const restrictedFileTypes = restrictedFileTypeList ?? [];
  for (const file of files) {
    const fileName = file.name;
    if (isFileTypeRestricted(fileName, restrictedFileTypes, onTypeMissmatch)) {
      return false;
    }
    if (isFileNameTooLong(fileName, maxFileLength, onFilenameLengthExceeded)) {
      return false; // files are not allowed
    }

    if (isFileTypeBad(fileName, fileTypes, onTypeMissmatch)) {
      return false; // files are not allowed
    }
  }

  return true;
};
const generateInputValue = function (files) {
  return [...files].map(({
    name
  }) => `"${name}"`).join(', ');
};
const processFiles = (props, files) => {
  let value = '';

  // send event so that component can do more when needed
  const filesSelected = [];
  const maxSize = props.maximumFileSize;
  let maximumFileSizeExceeded = false;
  let maxFileSizeExceededFile = null;
  if (files && files?.length > 0) {
    for (const file of files) {
      const fileSize = computeFileSize(file);
      if (maxSize && fileSize > maxSize) {
        filesSelected.push({
          value: file,
          maxFileSizeExceeded: true
        });
        maximumFileSizeExceeded = true;
        if (!maxFileSizeExceededFile) {
          maxFileSizeExceededFile = file;
        }
      } else {
        filesSelected.push({
          value: file,
          maxFileSizeExceeded: false
        });
      }
    }
    props.onFileSelection?.({
      fileSelectedInfo: filesSelected
    });
    if (maximumFileSizeExceeded && maxFileSizeExceededFile) {
      fireMaxFileSizeExceedEvent(maxFileSizeExceededFile, props.maximumFileSize, props.onFileSizeExceeded);
    }
    if (!maximumFileSizeExceeded && areFilesAllowed(files, props.fileType, props.restrictedFileType, props.maximumFilenameLength, props.onFilenameLengthExceeded, props.onTypeMissmatch)) {
      props.onFileAllowed?.({});
      if (props.multiple && files.length > 1) {
        value = generateInputValue(files);
      } else {
        value = files?.item(0)?.name ?? '';
      }
      props.onChange?.({
        newValue: value,
        files,
        fileName: files[0].name
      });
    }
  }
  return {
    value,
    filesSelected
  };
};

// ***************************************************
//
// methods to support uploadFiles
//
// ****************************************************

const getFormData = (files, name, additionalData) => {
  const formData = new FormData();
  const sName = name ?? 'file';
  formData.append('_charset_', 'UTF-8');
  formData.append(`${sName}-data`, additionalData ?? '');
  for (const file of files) {
    if (file instanceof window.Blob && file.name) {
      formData.append(sName, file, file.name);
    } else {
      formData.append(sName, file);
    }
  }
  return formData;
};
const getDataWithoutForm = (files, name = 'file[]', additionalData) => {
  const data = {};
  for (const file of files) {
    data[file.name] = file;
  }
  data._charset_ = 'UTF-8';
  data[`${name}-data`] = additionalData ?? '';
  return data;
};
const getRequestHeaders = headerParameters => {
  const headerObj = {};
  if (headerParameters) {
    for (const headerParameter of headerParameters) {
      headerObj[headerParameter.name] = headerParameter.value;

      // Browser handles content-type for FormData for Axios.
      // The XMLHttpRequest object will be used for upload
      // when a charset is specified in the content-type
      // Axios does not honor the charset in the content-type
      // Returning undefined says to use the XMLHttpRequest object
      if (headerParameter.name.toLowerCase().indexOf('content-type') > -1 && headerParameter.value.toLowerCase().indexOf('charset') > -1) {
        return undefined; // not using headerObj in this instance, end processing
      }
    }
  }

  return headerObj;
};
const getFirstFileName = files => {
  return files.length > 0 ? files[0].name : '';
};
const addRequestHeadersToClient = (xhr, headerParams) => {
  const headerParameters = headerParams;
  if (headerParameters) {
    for (const headerParameter of headerParameters) {
      xhr.setRequestHeader(headerParameter.name, headerParameter.value);
    }
  }
};
const addParametersToFormData = (formData, fuParameters) => {
  if (fuParameters) {
    for (const fuParameter of fuParameters) {
      formData.append(fuParameter.name, fuParameter.value);
    }
  }
  return null;
};
const addReadyState = (xhr, formData, headerParameters, onUploadFailed, onUploadCompleted) => {
  xhr.onreadystatechange = () => {
    if (xhr.readyState === xhr.DONE) {
      const uploadStatus = xhr.status;
      const rawResponse = xhr.response;
      const responseContent = xhr.responseText;

      // Parse the http-header into a map
      // Code from HTMLCommons version
      const plainHeader = xhr.getAllResponseHeaders();
      const headers = [];
      if (plainHeader) {
        const headerLines = plainHeader.split('\u000d\u000a');
        for (const headerLine of headerLines) {
          const headerIndex = headerLine.indexOf('\u003a\u0020');
          headers[headerLine.substring(0, headerIndex)] = headerLine.substring(headerIndex + 2);
        }
      }
      if (uploadStatus === 0 || uploadStatus >= 200 && uploadStatus < 400) {
        onUploadCompleted?.({
          data: xhr.response.data,
          status: xhr.status,
          statusText: xhr.statusText,
          formData,
          headerMap: headers,
          requestHeaders: headerParameters,
          response: rawResponse,
          responseContent
        });
      } else {
        onUploadFailed?.({
          errorObject: responseContent
        });
      }
    }
  };
};
const getParameters = fuParameters => {
  const parameters = {};
  if (fuParameters) {
    for (const fuParameter of fuParameters) {
      parameters[fuParameter.name] = fuParameter.value;
    }
  }
  return parameters;
};

// upload Files
export const upload = async (props, checkedFiles) => {
  const files = checkedFiles || [];
  const headers = getRequestHeaders(props?.headerParameters);
  const fileName = getFirstFileName(files);
  const withCredentials = props.withCredentials ?? false;

  // when the content type and the charset
  // are specified then the XMLHttpRequest
  // object has to be used because of
  // defects in Axios
  if (!headers) {
    const formData = getFormData(files, props?.name, props?.additionalData);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', props.uploadUrl, true);
    xhr.withCredentials = withCredentials;
    addRequestHeadersToClient(xhr, props.headerParameters);
    addParametersToFormData(formData, props.parameters);
    addReadyState(xhr, formData, props.headerParameters, props.onUploadFailed);
    props.onUploadStart?.({
      formData,
      fileName
    });
    xhr.send(formData);
  } else {
    let dataToPost;
    if (props?.postWithoutForm) {
      dataToPost = getDataWithoutForm(files, props?.name, props?.additionalData);
    } else {
      dataToPost = getFormData(files, props?.name, props?.additionalData);
    }
    const params = getParameters(props.parameters);
    const configObject = {
      headers,
      params,
      responseEncoding: 'utf8',
      withCredentials
    };
    props.onUploadStart?.({
      dataWithoutForm: props?.postWithoutForm && dataToPost,
      formData: dataToPost,
      fileName
    });
    await axios.post(props.uploadUrl, dataToPost, configObject).then(response => {
      logger.info('The File was successfully uploaded.');
      props.onUploadCompleted?.({
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        formData: dataToPost,
        dataWithoutForm: props?.postWithoutForm && dataToPost,
        headerMap: headers,
        requestHeaders: props.headerParameters,
        response,
        responseContent: response.toString()
      });
    }).catch(error => {
      logger.info(`The file could not be uploaded due to the following error: ${error}`);
      props.onUploadFailed?.({
        errorObject: error
      });
      if (error.response) {
        logger.info(error.response.data);
        logger.info(error.response.status);
        logger.info(error.response.headers);
      } else if (error.request) {
        logger.info(`No response was received.`);
        logger.info(error.request);
      } else {
        logger.info(`An error occurred while the request was being set up.`);
      }
    });
  }
};
export const checkAndUploadFiles = (props, files, triggerUpload) => {
  const filesToProcess = files || undefined;
  const {
    value,
    filesSelected
  } = processFiles(props, filesToProcess); // returns checked

  // if there is a value then the upload is okay to do
  // otherwise the upload should not happen
  if (filesToProcess && filesToProcess.length > 0 && value && value.trim().length > 0 && (!props?.uploadOnRequest || triggerUpload)) {
    upload(props, files);
  }
  return {
    value,
    filesSelected
  };
};

// ***************************************************
//
// methods to support create FilePicker
//
// ****************************************************

// accept list
// if a restricted list (restrictedFileTypes)
// then remove those
// as acceptable file types from
// the file type list (fileType)
const getFileTypes = (fileTypes, restrictedFileTypes) => {
  const finalFileTypes = [];
  if (restrictedFileTypes && restrictedFileTypes.length > 0 && fileTypes && fileTypes.length > 0) {
    for (const fileType of fileTypes) {
      if (restrictedFileTypes.indexOf(fileType) === -1) {
        finalFileTypes.push(fileType);
      }
    }
    return finalFileTypes && '.'.concat(finalFileTypes.join(',.'));
  }
  return fileTypes && '.'.concat(fileTypes.join(',.'));
};
export const openFilePickerAndUploadFiles = (filePickerProps, onFilePickerChange) => {
  const defaultName = 'fileUploader';

  // Create Input component
  // And set properties on the Input component
  const inputComponent = document.createElement('input');
  inputComponent.type = 'file';
  inputComponent.multiple = filePickerProps.multiple || false;
  inputComponent.accept = getFileTypes(filePickerProps.fileType, filePickerProps.restrictedFileType) || '';
  inputComponent.name = filePickerProps.name || defaultName;
  inputComponent.oninput = event => {
    const fileInput = event.target;
    checkAndUploadFiles(filePickerProps, fileInput.files || undefined, true);
    onFilePickerChange?.();
  };
  inputComponent.click();
  return inputComponent;
};
//# sourceMappingURL=FileUploaderUtility.js.map